import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { colors } from '../theme/colors';
import { getCategoriesCached, getSubCategoriesCached, getUnitsBankCached, upsertLocalMaster, invalidateMaster } from '../utils/masterDataStore';

// Same suggestions as the unit box in Chart of Inventory.
const UNIT_SUGGESTIONS = ['Pcs', 'Kg', 'Gm', 'Ltr', 'Ml', 'Mtr', 'Ft', 'Box', 'Pack', 'Dozen', 'Set', 'Pair'];

/**
 * QuickAddItemModal — a lightweight "create item on the fly" dialog for the
 * Sale Invoice / Purchase Invoice line grids. Captures only the essentials
 * (name, purchase price, sale price, category, sub-category, unit); unit
 * defaults to Pcs (any unit can be typed or picked) and the category
 * auto-selects the first one. The full item can still be edited later in the
 * Chart of Inventory master. On success it calls onCreated(newItem) with the
 * full item object (incl. the new id) so the caller can drop it straight into
 * the current line.
 */
const QuickAddItemModal = ({ companyId, existingItems = [], onCreated, onClose }) => {
  const [itemName, setItemName] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [subCategories, setSubCategories] = useState([]);
  const [subCategoryId, setSubCategoryId] = useState('');
  const [unit, setUnit] = useState('Pcs');
  const [unitList, setUnitList] = useState(UNIT_SUGGESTIONS);
  const [saving, setSaving] = useState(false);

  // Load categories and auto-select the first (per requirement).
  // Firebase cost: getCategoriesCached fetches the (small) category list once per
  // session and serves every later open from memory — opening this dialog ten
  // times in a row used to re-read the whole collection ten times.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!companyId) return;
      try {
        const rows = await getCategoriesCached(companyId);
        const cats = [...rows].sort((a, b) => (a.categoryName || '').localeCompare(b.categoryName || ''));
        if (cancelled) return;
        setCategories(cats);
        setCategoryId(cats[0]?.id || '');
      } catch (e) { console.error('Load categories failed:', e); }
      // Sub-categories and the company's units list (both cached per session).
      try {
        const [subs, bank] = await Promise.all([getSubCategoriesCached(companyId), getUnitsBankCached(companyId)]);
        if (cancelled) return;
        setSubCategories((subs || []).filter(s => s.isDeleted !== true).sort((a, b) => (a.subCategoryName || '').localeCompare(b.subCategoryName || '')));
        const fromBank = (bank || []).map(u => u.unitCode).filter(Boolean);
        setUnitList(Array.from(new Set([...fromBank, ...UNIT_SUGGESTIONS])));
      } catch (e) { console.error('Load sub-categories / units failed:', e); }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  const subCategoryOptions = subCategories.filter(s => s.categoryId === categoryId);

  // Mirror the Chart of Inventory item-code scheme (ITM-00001) using whatever
  // items the caller already has loaded.
  const genCode = () => {
    const maxCode = existingItems.reduce((max, it) => {
      const num = parseInt(String(it.itemCode || '').replace('ITM-', ''), 10) || 0;
      return num > max ? num : max;
    }, 0);
    return `ITM-${String(maxCode + 1).padStart(5, '0')}`;
  };

  const handleSave = async () => {
    if (!itemName.trim()) { alert('Item name is required'); return; }
    if (!companyId) return;
    setSaving(true);
    try {
      // Minimal but valid item — mirrors the defaults the full master writes so
      // the item behaves normally everywhere (stock, costing, reports).
      const itemData = {
        itemCode: genCode(),
        itemName: itemName.trim(),
        description: itemName.trim(),
        unitOfMeasure: unit.trim() || 'Pcs',
        barcode: '',
        hsCode: '',
        purchasePrice: parseFloat(purchasePrice) || 0,
        salePrice: parseFloat(salePrice) || 0,
        minimumPrice: 0,
        wholesalePrice: 0,
        dealerPrice: 0,
        mrp: 0,
        taxable: false,
        gstRate: 0,
        trackingType: 'None',
        reorderQuantity: 0,
        averageCost: 0,
        categoryId: categoryId || (categories[0]?.id || ''),
        subCategoryId: subCategoryOptions.some(s => s.id === subCategoryId) ? subCategoryId : '',
        imageUrl: '',
        units: [],
        defaultPurchaseUnit: '',
        defaultSaleUnit: '',
        itemType: 'product',
        hasSize: false,
        sizeUnit: 'inches',
        soldByWeight: false,
      };
      const docRef = await addDoc(collection(db, `companies/${companyId}/inventoryItems`), {
        ...itemData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Write the new item into this device's local master cache IMMEDIATELY.
      //
      // Why (bug fixed 2026-09-15): without this the item existed in Firestore
      // but not in the cache every OTHER screen reads from. It worked on the
      // invoice that created it — because that form pushes it into its own React
      // state — and then vanished: not in the POS, not in a second invoice, not
      // in the item search, until the cache's next change-check or a hard
      // refresh. This is the same write-through the full item master already
      // does (ChartOfInventory.js). itemData carries no serverTimestamp
      // sentinels (those are added in the spread above), so it is plain,
      // storable data.
      await upsertLocalMaster(companyId, 'inventoryItems', { id: docRef.id, ...itemData });
      invalidateMaster(companyId, 'inventoryItems'); // reconcile in the background

      onCreated?.({ id: docRef.id, ...itemData });
    } catch (e) {
      console.error('Quick add item failed:', e);
      alert('Failed to add item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <h3>Quick Add Item</h3>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </Header>
        <Body>
          <Field>
            <label>Item Name *</label>
            <input
              autoFocus
              value={itemName}
              onChange={e => setItemName(e.target.value)}
              placeholder="e.g. Copper Pipe 1/2"
              onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
            />
          </Field>
          <Row>
            <Field>
              <label>Purchase Price</label>
              <input type="number" value={purchasePrice} onChange={e => setPurchasePrice(e.target.value)} placeholder="0" />
            </Field>
            <Field>
              <label>Sale Price</label>
              <input type="number" value={salePrice} onChange={e => setSalePrice(e.target.value)} placeholder="0" />
            </Field>
          </Row>
          <Row>
            <Field>
              <label>Category</label>
              <select value={categoryId} onChange={e => { setCategoryId(e.target.value); setSubCategoryId(''); }}>
                {categories.length === 0 && <option value="">(No categories)</option>}
                {categories.map(c => <option key={c.id} value={c.id}>{c.categoryName}</option>)}
              </select>
            </Field>
            <Field>
              <label>Sub-Category</label>
              <select value={subCategoryId} onChange={e => setSubCategoryId(e.target.value)} data-quick-subcategory>
                <option value="">{subCategoryOptions.length ? '-- None --' : '(No sub-categories)'}</option>
                {subCategoryOptions.map(s => <option key={s.id} value={s.id}>{s.subCategoryName}</option>)}
              </select>
            </Field>
          </Row>
          <Row>
            <Field>
              <label>Unit</label>
              <input list="quick-add-units" value={unit} onChange={e => setUnit(e.target.value)} placeholder="Pcs, Kg, Mtr, ..." data-quick-unit />
              <datalist id="quick-add-units">
                {unitList.map(u => <option key={u} value={u} />)}
              </datalist>
            </Field>
            <Field />
          </Row>
          <Hint>Only the essentials are captured here. You can refine this item later in Chart of Inventory.</Hint>
        </Body>
        <Footer>
          <CancelBtn onClick={onClose} disabled={saving}>Cancel</CancelBtn>
          <SaveBtn onClick={handleSave} disabled={saving}>{saving ? 'Adding…' : 'Add Item'}</SaveBtn>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default QuickAddItemModal;

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center; z-index: 3000;
`;
const Modal = styled.div`
  background: #fff; border-radius: 10px; width: 420px; max-width: 94vw;
  box-shadow: 0 12px 40px rgba(0,0,0,0.25); overflow: hidden;
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid #eee;
  h3 { margin: 0; font-size: 16px; color: ${colors.maroon || '#800020'}; }
`;
const CloseBtn = styled.button`
  border: none; background: none; font-size: 22px; line-height: 1; cursor: pointer; color: #888;
`;
const Body = styled.div`padding: 18px;`;
const Row = styled.div`display: flex; gap: 12px; & > * { flex: 1; }`;
const Field = styled.div`
  margin-bottom: 12px; display: flex; flex-direction: column; min-width: 0;
  label { font-size: 12px; color: #555; margin-bottom: 4px; }
  input, select {
    width: 100%; box-sizing: border-box;
    padding: 8px 10px; border: 1px solid #ccc; border-radius: 6px; font-size: 14px;
    &:disabled { background: #f4f4f4; color: #888; }
  }
`;
const Hint = styled.div`font-size: 11px; color: #999; margin-top: 4px;`;
const Footer = styled.div`
  display: flex; justify-content: flex-end; gap: 10px; padding: 14px 18px; border-top: 1px solid #eee;
`;
const CancelBtn = styled.button`
  padding: 8px 16px; border: 1px solid #ccc; background: #fff; border-radius: 6px; cursor: pointer;
`;
const SaveBtn = styled.button`
  padding: 8px 18px; border: none; background: ${colors.maroon || '#800020'}; color: #fff;
  border-radius: 6px; cursor: pointer; font-weight: 600;
  &:disabled { opacity: 0.6; cursor: default; }
`;
